// ============================================================================
// BOQ — building-typology vocabulary (boq.building_typologies)
// ============================================================================
// The controlled list of building typologies used to tag SD area-rates. Seeded
// from the firm-wide Accounting -> Categories list (public.project_categories)
// and extensible with custom labels (is_custom=true). Reads/writes use raw
// PostgREST on the boq schema, same as the rest of the BOQ app. Writes are
// restricted to admin/supervisor by RLS.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

const SELECT = 'id, label, sort_order, is_active, is_custom, source_category_id';

const mapRow = (r) => ({
  id: r.id,
  label: r.label,
  sortOrder: r.sort_order,
  isActive: r.is_active,
  isCustom: r.is_custom,
  sourceCategoryId: r.source_category_id,
});

export const useBoqBuildingTypologies = ({ enabled = true } = {}) => {
  const [typologies, setTypologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fErr } = await supabase
        .schema('boq')
        .from('building_typologies')
        .select(SELECT)
        .order('sort_order', { ascending: true })
        .order('label', { ascending: true });
      if (fErr) throw fErr;
      setTypologies((data || []).map(mapRow));
    } catch (err) {
      console.error('[useBoqBuildingTypologies] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'typologies' }));
    } finally {
      setLoading(false);
    }
  }, []);

  const requireSession = useCallback(async () => {
    const { data, error: sErr } = await supabase.auth.getSession();
    if (sErr || !data?.session) throw new Error('Please sign in.');
  }, []);

  // Add a custom typology. Returns the created row on success.
  const createTypology = useCallback(async (label) => {
    const clean = (label || '').trim();
    if (!clean) return { success: false, error: 'Enter a label.' };
    try {
      await requireSession();
      const maxOrder = typologies.reduce((m, t) => Math.max(m, t.sortOrder || 0), 0);
      const { data, error: insErr } = await supabase
        .schema('boq')
        .from('building_typologies')
        .insert({ label: clean, sort_order: maxOrder + 1, is_custom: true })
        .select(SELECT)
        .single();
      if (insErr) throw insErr;
      const row = mapRow(data);
      setTypologies((prev) => [...prev, row]);
      return { success: true, typology: row };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'typology' }) };
    }
  }, [typologies, requireSession]);

  const updateTypology = useCallback(async (id, updates) => {
    try {
      await requireSession();
      const patch = {};
      if (updates.label !== undefined) patch.label = updates.label.trim();
      if (updates.isActive !== undefined) patch.is_active = updates.isActive;
      if (updates.sortOrder !== undefined) patch.sort_order = updates.sortOrder;
      const { data, error: upErr } = await supabase
        .schema('boq')
        .from('building_typologies')
        .update(patch)
        .eq('id', id)
        .select(SELECT)
        .single();
      if (upErr) throw upErr;
      const row = mapRow(data);
      setTypologies((prev) => prev.map((t) => (t.id === id ? row : t)));
      return { success: true, typology: row };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'typology' }) };
    }
  }, [requireSession]);

  useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);

  return { typologies, loading, error, refresh: fetchAll, createTypology, updateTypology };
};

export default useBoqBuildingTypologies;
